const COPY = {
  en: {
    nav: { projects: 'Projects', skills: 'Skills', about: 'About', testimonials: 'Testimonials', contact: 'Contact' },
    hero: {
      eyebrow: 'Senior Technical Program Manager | AI/ML Program Strategy & Delivery | Enterprise AI Programs | Ex-Amazon & Pinterest',
      greeting: 'Hi, I\'m Javier Bonilla.',
      tagline: 'I turn ambiguity into roadmaps that ship.',
      body: 'Specializing in AI-powered platform programs across advertising and e‑commerce.',
      cta_primary: 'See selected work',
      cta_secondary: 'Read about me',
    },
    stats: [
      { value: '$100M+', label: 'in incremental deal value delivered' },
      { value: '$9.7M', label: 'in costs cut directing automation programs' },
      { value: '30%', label: 'faster decision-making across programs' },
    ],
    domains: {
      label: 'Domains',
      items: ['AI/ML Strategy', 'Monetization Systems', 'Enterprise Tooling'],
    },
    certifications: {
      title: 'Certifications',
      sub: 'Click to verify credentials.',
      items: [
        {
          name: 'AI & Machine Learning: Code, Train, Deploy',
          issuer: 'Stanford Online',
          year: '2024',
          href: 'https://credsverse.com/credentials/f2d0a06f-bd8b-4f52-9570-94a9c2cf671f',
          verifiable: true,
        },
        {
          name: 'Software Product Strategy',
          issuer: 'Northwestern Kellogg',
          year: '2023',
          href: 'https://badges.parchment.com/public/assertions/ezhv1hxoRlyZGFeK71KKCw',
          verifiable: true,
        },
        {
          name: 'Lean Six Sigma Black Belt',
          issuer: 'ASQ',
          year: '2021',
          href: '/assets/black-belt-cert.jpg',
          verifiable: true,
        },
      ],
    },
    projects: {
      title: 'Selected work',
      sub: 'A few programs I\'ve led end-to-end.',
      items: [
        {
          company: '',
          title: 'Faster project planning for up to 30% with AI',
          body: 'Imagine the ability to handle 100+ concurrent projects, with multi-user intake in real time. I built an ML-informed scoring app that eliminates poor intake quality, misaligned stakeholders, and unclear risk ownership.',
          learnMore: 'Learn more',
          learnMoreHref: 'https://github.com/Jay06eng/decision-assistant-ai-tpm?utm_source=projects_page&utm_medium=portfolio&utm_campaign=site',
          media: { type: 'video', src: '/assets/project-1-demo.mov', poster: '' },
          metrics: [],
        },
        {
          company: '',
          title: 'Automating advertisers credit line reviews by 25%',
          body: 'Automated the advertiser credit-line review process and shipped it as a user-friendly Salesforce feature for the Finance team. Used correlation analysis and regression to surface the five factors that matter most for forecasting credit-line behavior.',
          learnMore: 'Learn more',
          learnMoreHref: 'https://github.com/Jay06eng/credit-line-approval-ml',
          media: { type: 'image', src: '/assets/credit-line-thumbnail.png', alt: 'Credit-line approval ML model overview — Python+Colab analysis to Salesforce feature' },
          metrics: [],
        },
      ],
    },
    skills: {
      main: {
        label: 'Primary skills & tools',
        items: [
          { name: 'Python', mono: 'Py' },
          { name: 'SQL', mono: 'SQL' },
          { name: 'LLMs', mono: 'LM' },
          { name: 'Azure DevOps', mono: 'Az' },
          { name: 'Salesforce', mono: 'SF' },
          { name: 'Oracle', mono: 'Or' },
          { name: 'Ads Manager', mono: 'AM' },
          { name: 'AWS', mono: 'AWS' },
          { name: 'Git', mono: 'Git' },
        ],
      },
      complementary: {
        label: 'Complementary skills & tools',
        items: [
          { name: 'Jira', mono: 'Ji' },
          { name: 'Notion', mono: 'N' },
          { name: 'Confluence', mono: 'Cf' },
          { name: 'MySQL', mono: 'My' },
          { name: 'Postgres', mono: 'Pg' },
          { name: 'MongoDB', mono: 'Mo' },
          { name: 'API Integration', mono: 'API' },
          { name: 'Claude Code', mono: 'CC' },
          { name: 'Codex', mono: 'Cx' },
          { name: 'GitHub Copilot', mono: 'Co' },
          { name: 'VS Code', mono: 'VS' },
        ],
      },
    },
    about: {
      title: 'What drives me',
      paragraphs: [
        'I enjoy turning messy, real-world challenges into systems that are simpler, clearer, and built to scale.',
      ],
      timeline: [
        { when: 'Aug 2025 — Present', duration: '10 mos', what: 'Senior TPM, AI/ML Program Strategy & Delivery', where: 'CommonDevOps' },
        { when: 'Apr 2025 — Sep 2025', duration: '6 mos', what: 'Senior Technical Program Manager', where: 'Xenon arc' },
        { when: 'Sep 2022 — Jun 2024', duration: '1 yr 10 mos', what: 'Senior Strategy & Operations Manager', where: 'Pinterest' },
        { when: 'Jul 2020 — Aug 2022', duration: '2 yrs 2 mos', what: 'Program Manager, Transformation', where: 'Amazon' },
        { when: 'Feb 2018 — Jul 2020', duration: '2 yrs 6 mos', what: 'Process Engineering Manager', where: 'Amazon' },
        { when: 'Jul 2016 — Feb 2018', duration: '1 yr 8 mos', what: 'Process Engineer', where: 'Amazon' },
      ],
    },
    testimonials: {
      title: 'What colleagues say',
      sub: 'Verified LinkedIn recommendations. Click to see the original.',
      cta: 'Verify on LinkedIn',
      href: 'https://www.linkedin.com/in/javierbonillab/details/recommendations/?detailScreenTabIndex=0',
      items: [
        {
          quote: `It is rare to work with someone as broadly skilled as Javier. While his title is "Technical Project Manager," that only tells part of the story. What stands out is his strong computer science background and his understanding of programming, AI, and machine learning. That combination makes a real difference when building roadmaps that are strategic, practical, and grounded in reality.`,
          name: 'Aaron Schneider',
          title: 'Automation Expert · 25+ yrs enterprise software',
          relation: 'Colleague',
          date: 'Apr 2026',
        },
        {
          quote: `'Efficient problem solver' and 'Expert strategist' are two things which come right off the bat when I think of Javier. A great leader, who didn't aim at managing people but leading them to grow, realize their potential and deliver results to gain knowledge, visibility and a path to progress in one's career.`,
          name: 'Chirag Jain',
          title: 'Sr. TPM, Prime Video · Amazon',
          relation: 'Direct report',
          date: 'Apr 2022',
        },
        {
          quote: `Javier is a highly skilled and accomplished Program Manager. His experience at Amazon has given him the tools to quickly adapt to new environments and navigate complex and/or ever changing projects to meet and exceed expectations. His primary experience in leading process improvement initiatives across multiple XFN teams has enabled him to work with multiple stakeholders while identifying root cause issues in large scale processes.`,
          name: 'Jess McCall',
          title: 'Technical Program Manager · Netflix',
          relation: 'Senior peer',
          date: 'Apr 2022',
        },
      ],
    },
    contact: {
      title: "Let's work together.",
      sub: "I'm open to Senior TPM opportunities across AI/ML program strategy & delivery, enterprise AI enablement, systems orchestration, and operational tooling.",
      email: 'jbonilla06@gmail.com',
      cta: 'Email me',
      socials: [
        { label: 'LinkedIn', href: 'https://www.linkedin.com/in/javierbonillab/' },
        { label: 'GitHub', href: 'https://github.com/Jay06eng' },
      ],
    },
    footer: 'Designed & built by Javier Bonilla · 2026',
  },
  es: {
    nav: { projects: 'Proyectos', skills: 'Skills', about: 'Sobre mí', testimonials: 'Testimonios', contact: 'Contacto' },
    hero: {
      eyebrow: 'Senior Technical Program Manager | AI/ML Program Strategy & Delivery | Programas IA enterprise | Ex-Amazon & Pinterest',
      greeting: 'Hola, soy Javier Bonilla.',
      tagline: 'Convierto la ambigüedad en roadmaps que se entregan.',
      body: 'Especializado en programas de plataformas basadas en IA en publicidad y e‑commerce.',
      cta_primary: 'Ver trabajo seleccionado',
      cta_secondary: 'Sobre mí',
    },
    stats: [
      { value: '$100M+', label: 'en valor incremental entregado' },
      { value: '$9.7M', label: 'en costos reducidos dirigiendo programas de automatización' },
      { value: '30%', label: 'más rápido en toma de decisiones' },
    ],
    domains: {
      label: 'Dominios',
      items: ['AI/ML Strategy', 'Sistemas de monetización', 'Herramientas enterprise'],
    },
    certifications: {
      title: 'Certificaciones',
      sub: 'Haz clic para verificar las credenciales.',
      items: [
        {
          name: 'AI & Machine Learning: Code, Train, Deploy',
          issuer: 'Stanford Online',
          year: '2024',
          href: 'https://credsverse.com/credentials/f2d0a06f-bd8b-4f52-9570-94a9c2cf671f',
          verifiable: true,
        },
        {
          name: 'Software Product Strategy',
          issuer: 'Northwestern Kellogg',
          year: '2023',
          href: 'https://badges.parchment.com/public/assertions/ezhv1hxoRlyZGFeK71KKCw',
          verifiable: true,
        },
        {
          name: 'Lean Six Sigma Black Belt',
          issuer: 'ASQ',
          year: '2021',
          href: '/assets/black-belt-cert.jpg',
          verifiable: true,
        },
      ],
    },
    projects: {
      title: 'Trabajo seleccionado',
      sub: 'Programas que he liderado de principio a fin.',
      items: [
        {
          company: '',
          title: 'Planeación de proyectos hasta 30% más rápida con IA',
          body: 'Imagina manejar 100+ proyectos simultáneos con intake multi-usuario en tiempo real. Construí una app de scoring informada por ML que elimina la mala calidad de intake, los stakeholders desalineados y la propiedad poco clara del riesgo.',
          learnMore: 'Saber más',
          learnMoreHref: 'https://github.com/Jay06eng/decision-assistant-ai-tpm?utm_source=projects_page&utm_medium=portfolio&utm_campaign=site',
          media: { type: 'video', src: '/assets/project-1-demo.mov', poster: '' },
          metrics: [],
        },
        {
          company: '',
          title: 'Automatizando las revisiones de líneas de crédito de anunciantes en 25%',
          body: 'Automaticé el proceso de revisión de líneas de crédito de anunciantes y lo entregué como una feature user-friendly de Salesforce para el equipo de Finanzas. Usé análisis de correlación y regresión para identificar los cinco factores que más importan al forecastear el comportamiento de la línea de crédito.',
          learnMore: 'Saber más',
          learnMoreHref: 'https://github.com/Jay06eng/credit-line-approval-ml',
          media: { type: 'image', src: '/assets/credit-line-thumbnail.png', alt: 'Credit-line approval ML model overview — Python+Colab analysis to Salesforce feature' },
          metrics: [],
        },
      ],
    },
    skills: {
      main: {
        label: 'Skills y herramientas principales',
        items: [
          { name: 'Python', mono: 'Py' },
          { name: 'SQL', mono: 'SQL' },
          { name: 'LLMs', mono: 'LM' },
          { name: 'Azure DevOps', mono: 'Az' },
          { name: 'Salesforce', mono: 'SF' },
          { name: 'Oracle', mono: 'Or' },
          { name: 'Ads Manager', mono: 'AM' },
          { name: 'AWS', mono: 'AWS' },
          { name: 'Git', mono: 'Git' },
        ],
      },
      complementary: {
        label: 'Skills y herramientas complementarias',
        items: [
          { name: 'Jira', mono: 'Ji' },
          { name: 'Notion', mono: 'N' },
          { name: 'Confluence', mono: 'Cf' },
          { name: 'MySQL', mono: 'My' },
          { name: 'Postgres', mono: 'Pg' },
          { name: 'MongoDB', mono: 'Mo' },
          { name: 'API Integration', mono: 'API' },
          { name: 'Claude Code', mono: 'CC' },
          { name: 'Codex', mono: 'Cx' },
          { name: 'GitHub Copilot', mono: 'Co' },
          { name: 'VS Code', mono: 'VS' },
        ],
      },
    },
    about: {
      title: 'Lo que me mueve',
      paragraphs: [
        'Disfruto convertir desafíos reales y desordenados en sistemas más simples, claros y diseñados para escalar.',
      ],
      timeline: [
        { when: 'Ago 2025 — Presente', duration: '10 meses', what: 'Senior TPM, AI/ML Program Strategy & Delivery', where: 'CommonDevOps' },
        { when: 'Abr 2025 — Sep 2025', duration: '6 meses', what: 'Senior Technical Program Manager', where: 'Xenon arc' },
        { when: 'Sep 2022 — Jun 2024', duration: '1 año 10 meses', what: 'Senior Strategy & Operations Manager', where: 'Pinterest' },
        { when: 'Jul 2020 — Ago 2022', duration: '2 años 2 meses', what: 'Program Manager, Transformation', where: 'Amazon' },
        { when: 'Feb 2018 — Jul 2020', duration: '2 años 6 meses', what: 'Process Engineering Manager', where: 'Amazon' },
        { when: 'Jul 2016 — Feb 2018', duration: '1 año 8 meses', what: 'Process Engineer', where: 'Amazon' },
      ],
    },
    testimonials: {
      title: 'Lo que dicen mis colegas',
      sub: 'Recomendaciones verificadas de LinkedIn. Haz clic para ver el original.',
      cta: 'Verificar en LinkedIn',
      href: 'https://www.linkedin.com/in/javierbonillab/details/recommendations/?detailScreenTabIndex=0',
      items: [
        {
          quote: `It is rare to work with someone as broadly skilled as Javier. While his title is "Technical Project Manager," that only tells part of the story. What stands out is his strong computer science background and his understanding of programming, AI, and machine learning. That combination makes a real difference when building roadmaps that are strategic, practical, and grounded in reality.`,
          name: 'Aaron Schneider',
          title: 'Automation Expert · 25+ yrs enterprise software',
          relation: 'Colega',
          date: 'Abr 2026',
        },
        {
          quote: `'Efficient problem solver' and 'Expert strategist' are two things which come right off the bat when I think of Javier. A great leader, who didn't aim at managing people but leading them to grow, realize their potential and deliver results to gain knowledge, visibility and a path to progress in one's career.`,
          name: 'Chirag Jain',
          title: 'Sr. TPM, Prime Video · Amazon',
          relation: 'Reporte directo',
          date: 'Abr 2022',
        },
        {
          quote: `Javier is a highly skilled and accomplished Program Manager. His experience at Amazon has given him the tools to quickly adapt to new environments and navigate complex and/or ever changing projects to meet and exceed expectations. His primary experience in leading process improvement initiatives across multiple XFN teams has enabled him to work with multiple stakeholders while identifying root cause issues in large scale processes.`,
          name: 'Jess McCall',
          title: 'Technical Program Manager · Netflix',
          relation: 'Par senior',
          date: 'Abr 2022',
        },
      ],
    },
    contact: {
      title: 'Hablemos.',
      sub: 'Abierto a oportunidades de Senior TPM en estrategia y entrega de programas AI/ML, habilitación de IA enterprise, orquestación de sistemas y tooling operativo.',
      email: 'jbonilla06@gmail.com',
      cta: 'Escríbeme',
      socials: [
        { label: 'LinkedIn', href: 'https://www.linkedin.com/in/javierbonillab/' },
        { label: 'GitHub', href: 'https://github.com/Jay06eng' },
      ],
    },
    footer: 'Diseñado y construido por Javier Bonilla · 2026',
  },
};

export default COPY;
